"use client";

import { useEffect, useMemo, useState } from "react";
import {
  ClipboardList,
  CreditCard,
  ExternalLink,
  Info,
  MessageSquareText,
  Truck,
  User,
} from "lucide-react";
import { DrawerFieldShell, DrawerReadonlyValue, ImageViewer } from "@aera/data-table";
import { AeraImage } from "@/app/components/ui/AeraImage";
import { ReceivableAnnouncementAmountEditor } from "@/app/artifacts/table/ReceivableAnnouncementAmountEditor";
import { ReceivableCustomerNameEditor } from "@/app/artifacts/table/ReceivableCustomerNameEditor";
import { ReceivablePendingAddressEditor } from "@/app/artifacts/table/ReceivablePendingAddressEditor";
import type { PendingAddressDraft } from "@/app/artifacts/table/ReceivablePendingAddressEditor";
import { ReceivableSlipSenderEditor } from "@/app/artifacts/table/ReceivableSlipSenderEditor";
import { ReceivableFieldCell } from "@/app/artifacts/table/ReceivableFieldCell";
import type { ReceivableListItem } from "@/lib/pendingOrders";
import {
  isReceivableAddressItem,
  isReceivableAnnouncementItem,
  isReceivableSlipItem,
} from "@/lib/pendingOrders";
import {
  canEditPendingAddress,
  canEditPendingSlipSender,
  canEditReceivableAmount,
  canEditReceivableCustomerName,
} from "@/lib/receivableCorrections";
import { resolveReceivableDisplayStatus } from "@/lib/receivableDisplayStatus";
import { showToast } from "@/app/components/ui/Toast";
import { ReminderToneDrafts } from "@/app/components/ReminderToneDrafts";
import { getCopyReminders } from "@/lib/reminderTemplates";
import {
  groupReceivableDrawerFields,
  isMultilineReceivableField,
  isReceivableFieldEditable,
  receivableFieldCellType,
  resolveReceivableKindFromItem,
} from "./receivableDetailsUtils";

const DRAWER_HIDDEN_IMAGE_FIELD_KEYS = new Set([
  "image_url",
  "image",
  "file_url",
  "slip_image",
  "slip_url",
]);

const SECTION_ICONS: Record<string, typeof Info> = {
  PAYMENT: CreditCard,
  CUSTOMER: User,
  SHIPPING: Truck,
  DETAILS: ClipboardList,
};

const INPUT_CLASS =
  "w-full border-0 bg-transparent p-0 text-sm font-semibold leading-6 text-aera-charcoal shadow-none placeholder:text-aera-lightslate placeholder:font-normal focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:text-aera-lightslate";

function fieldValueForInput(value: string): string {
  const trimmed = value.trim();
  return trimmed === "—" ? "" : trimmed;
}

function parseAmountNumber(raw: string | null | undefined): number | null {
  const trimmed = raw?.trim();
  if (!trimmed || trimmed === "—") return null;
  const cleaned = trimmed.replace(/[^\d.-]/g, "");
  if (!cleaned) return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

function pickReminderDrafts(
  item: ReceivableListItem,
): { polite: string; direct: string } | null {
  if (resolveReceivableDisplayStatus(item) === "ลบรายการ") return null;
  if (resolveReceivableDisplayStatus(item) === "หมดอายุ") return null;
  if (isReceivableSlipItem(item)) return null;

  const name =
    item.fields
      .find((field) =>
        ["customer_name", "customer_ref", "addr_name", "recipient_name"].includes(
          field.key,
        ),
      )
      ?.value?.trim() || item.title?.trim();
  if (!name || name === "—") return null;

  const remaining =
    parseAmountNumber(
      item.fields.find((field) =>
        ["amount_remaining", "amount", "outstanding_amount", "outstanding"].includes(
          field.key,
        ),
      )?.value,
    ) ?? parseAmountNumber(item.amountLabel);
  if (remaining == null || remaining <= 0.009) return null;

  return getCopyReminders(name, remaining);
}

async function copyReminderDraft(
  message: string,
  onCopied: (ok: boolean) => void,
) {
  try {
    await navigator.clipboard.writeText(message);
    onCopied(true);
    showToast("คัดลอกร่างแล้ว");
    window.setTimeout(() => onCopied(false), 1500);
  } catch {
    onCopied(false);
    showToast("คัดลอกไม่สำเร็จ ลองอีกครั้ง", "error");
  }
}

function ReceivableFieldReadonly({
  fieldKey,
  label,
  value,
}: {
  fieldKey: string;
  label: string;
  value: string;
}) {
  const multiline = isMultilineReceivableField(fieldKey, value);

  return (
    <div className="space-y-1.5 px-4 py-3">
      <label className="block text-xs font-semibold leading-5 text-aera-lightslate">
        {label}
      </label>
      <div className="min-w-0">
        <DrawerReadonlyValue multiline={multiline}>
          <ReceivableFieldCell
            fieldKey={fieldKey}
            value={value}
            variant="drawer"
          />
        </DrawerReadonlyValue>
      </div>
    </div>
  );
}

function ReceivableFieldInput({
  fieldKey,
  label,
  value,
  multiline,
  disabled,
  onChange,
}: {
  fieldKey: string;
  label: string;
  value: string;
  multiline: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const inputId = `receivable-field-${fieldKey}`;
  const cellType = receivableFieldCellType(fieldKey);

  return (
    <div className="space-y-1.5 px-4 py-3">
      <label
        htmlFor={inputId}
        className="block text-xs font-semibold leading-5 text-aera-lightslate"
      >
        {label}
      </label>
      <DrawerFieldShell multiline={multiline} editable={!disabled}>
        {multiline ? (
          <textarea
            id={inputId}
            value={value}
            disabled={disabled}
            rows={3}
            placeholder="ยังไม่ระบุ"
            onChange={(event) => onChange(event.target.value)}
            className={`${INPUT_CLASS} min-h-[72px] resize-none`}
          />
        ) : (
          <input
            id={inputId}
            type={cellType === "tel" ? "tel" : "text"}
            value={value}
            disabled={disabled}
            placeholder="ยังไม่ระบุ"
            inputMode={cellType === "amount" ? "decimal" : undefined}
            onChange={(event) => onChange(event.target.value)}
            className={`${INPUT_CLASS} ${
              cellType === "amount" ? "tabular-nums tracking-tight" : ""
            }`}
          />
        )}
      </DrawerFieldShell>
    </div>
  );
}

export function ReceivableDetailsTab({
  item,
  disabled = false,
  imageUrl,
  kindLabel,
  amountLabel,
  viewerOpen,
  imageLoadFailed,
  onViewerOpenChange,
  onImageError,
  onDraftChange,
  ownerUserId = null,
  workspaceId = null,
  onAmountApplied,
  addressDraft = null,
  onAddressDraftChange,
  addressSaveBusy = false,
}: {
  item: ReceivableListItem;
  disabled?: boolean;
  imageUrl: string | null;
  kindLabel: string;
  amountLabel: string;
  viewerOpen: boolean;
  imageLoadFailed: boolean;
  onViewerOpenChange: (open: boolean) => void;
  onImageError: () => void;
  onDraftChange?: (item: ReceivableListItem) => void;
  ownerUserId?: string | null;
  workspaceId?: string | null;
  onAmountApplied?: () => void | Promise<void>;
  addressDraft?: PendingAddressDraft | null;
  onAddressDraftChange?: (draft: PendingAddressDraft) => void;
  addressSaveBusy?: boolean;
}) {
  const receivableKind = useMemo(
    () => resolveReceivableKindFromItem(item),
    [item],
  );
  const showSlipEvidence = receivableKind === "slip" || isReceivableSlipItem(item);
  const editPendingAddress =
    (receivableKind === "address" || isReceivableAddressItem(item)) &&
    canEditPendingAddress(item);
  const detailFields = useMemo(
    () => item.fields.filter((field) => !DRAWER_HIDDEN_IMAGE_FIELD_KEYS.has(field.key)),
    [item.fields],
  );
  const drawerSections = useMemo(
    () => groupReceivableDrawerFields(detailFields, receivableKind),
    [detailFields, receivableKind],
  );
  const addressEditorSections = useMemo(
    () =>
      drawerSections.filter(
        (section) =>
          section.label === "CUSTOMER" || section.label === "SHIPPING",
      ),
    [drawerSections],
  );
  const remainingSections = useMemo(
    () =>
      editPendingAddress
        ? drawerSections.filter(
            (section) =>
              section.label !== "CUSTOMER" && section.label !== "SHIPPING",
          )
        : drawerSections,
    [drawerSections, editPendingAddress],
  );

  const editableFields = useMemo(
    () => detailFields.filter((field) => isReceivableFieldEditable(field.key)),
    [detailFields],
  );

  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const [reminderCopied, setReminderCopied] = useState(false);

  const reminderDrafts = useMemo(() => pickReminderDrafts(item), [item]);

  useEffect(() => {
    setDraftValues(
      Object.fromEntries(
        editableFields.map((field) => [
          field.key,
          fieldValueForInput(field.value),
        ]),
      ),
    );
  }, [editableFields, item.mergeKey]);

  useEffect(() => {
    setReminderCopied(false);
  }, [item.mergeKey]);

  const handleFieldChange = (fieldKey: string, nextValue: string) => {
    setDraftValues((current) => {
      const next = { ...current, [fieldKey]: nextValue };
      if (!onDraftChange) return next;

      const amountKeys = new Set([
        "debt_amount",
        "debt",
        "balance",
        "outstanding_amount",
        "outstanding",
        "price",
      ]);

      const nextFields = item.fields.map((field) => ({
        ...field,
        value:
          field.key in next ? next[field.key] || "—" : field.value,
      }));

      const amountField = nextFields.find((field) =>
        amountKeys.has(field.key),
      );

      onDraftChange({
        ...item,
        fields: nextFields,
        amountLabel:
          amountField?.value && amountField.value !== "—"
            ? amountField.value
            : item.amountLabel,
      });

      return next;
    });
  };

  return (
    <div className="space-y-4">
      {showSlipEvidence || imageUrl ? (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => imageUrl && !imageLoadFailed && onViewerOpenChange(true)}
            disabled={!imageUrl || imageLoadFailed}
            className={`group relative flex h-24 w-full items-center gap-4 overflow-hidden rounded-lg bg-aera-white p-4 transition-all ${
              !imageUrl || imageLoadFailed
                ? "cursor-default"
                : "cursor-pointer hover:bg-aera-lightgray/50 active:scale-[0.99]"
            }`}
          >
            <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-md bg-aera-lightgray">
              {!imageUrl || imageLoadFailed ? (
                <div className="flex h-full w-full items-center justify-center text-[10px] font-semibold text-aera-lightslate">
                  ไม่พบรูป
                </div>
              ) : (
                <AeraImage
                  src={imageUrl}
                  alt="สลิป"
                  width={56}
                  height={56}
                  className="h-full w-full object-cover transition-transform group-hover:scale-110"
                  onError={onImageError}
                />
              )}
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-bold text-aera-gray">
                หลักฐานการทำรายการ
              </p>
              <p className="mt-0.5 text-xs font-medium text-aera-lightslate">
                {!imageUrl || imageLoadFailed
                  ? "ไม่พบรูปภาพ"
                  : "แตะเพื่อดูภาพขนาดเต็มและขยาย"}
              </p>
            </div>
            {imageUrl && !imageLoadFailed ? (
              <ExternalLink className="h-5 w-5 text-aera-lightslate transition-colors group-hover:text-aera-gray" />
            ) : null}
          </button>
          {imageUrl && !imageLoadFailed ? (
            <ImageViewer
              open={viewerOpen}
              onOpenChange={onViewerOpenChange}
              imageUrl={imageUrl}
              caption={`${kindLabel} · ${amountLabel}`}
            />
          ) : null}
        </div>
      ) : null}

      {editPendingAddress && addressDraft && onAddressDraftChange ? (
        <ReceivablePendingAddressEditor
          item={item}
          sections={addressEditorSections}
          draft={addressDraft}
          onDraftChange={onAddressDraftChange}
          disabled={disabled}
          busy={addressSaveBusy}
        />
      ) : null}

      {reminderDrafts ? (
        <div className="overflow-hidden rounded-2xl border border-(--aera-dt-border-soft) bg-(--aera-dt-surface-elevated)">
          <div className="flex items-center gap-2 border-b border-(--aera-dt-border-soft) bg-aera-white px-4 py-3">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-(--aera-dt-row-selected-surface) text-(--aera-dt-accent-hover)">
              <MessageSquareText className="h-4 w-4" />
            </div>
            <h3 className="text-sm font-bold tracking-tight text-aera-charcoal">
              ร่างข้อความทวง
            </h3>
          </div>
          <div className="space-y-2 px-4 py-3">
            <ReminderToneDrafts
              polite={reminderDrafts.polite}
              direct={reminderDrafts.direct}
              copied={reminderCopied}
              copyTestId="receivable-reminder-copy"
              onCopy={(message) => {
                void copyReminderDraft(message, setReminderCopied);
              }}
            />
          </div>
        </div>
      ) : null}

      {remainingSections.map((section) => {
        const SectionIcon = SECTION_ICONS[section.label] ?? Info;

        return (
          <div
            key={section.label}
            className="animate-in fade-in slide-in-from-top-2 duration-500 fill-mode-both"
          >
            <div className="overflow-hidden rounded-2xl border border-(--aera-dt-border-soft) bg-(--aera-dt-surface-elevated)">
              <div className="flex items-center gap-2 border-b border-(--aera-dt-border-soft) bg-aera-white px-4 py-3">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-(--aera-dt-row-selected-surface) text-(--aera-dt-accent-hover)">
                  <SectionIcon className="h-4 w-4" />
                </div>
                <h3 className="text-sm font-bold tracking-tight text-aera-charcoal">
                  {section.title}
                </h3>
              </div>
              <div className="divide-y divide-(--aera-dt-border-soft)">
                {section.fields.map((field) => {
                  const displayValue = field.value?.trim() || "—";

                  if (
                    field.key === "amount_original" &&
                    (receivableKind === "announcement" ||
                      isReceivableAnnouncementItem(item)) &&
                    canEditReceivableAmount(item)
                  ) {
                    return (
                      <ReceivableAnnouncementAmountEditor
                        key={field.key}
                        item={item}
                        label={field.label}
                        value={displayValue}
                        ownerUserId={ownerUserId}
                        workspaceId={workspaceId}
                        disabled={disabled}
                        onApplied={onAmountApplied}
                      />
                    );
                  }

                  if (
                    (field.key === "customer_ref" ||
                      field.key === "customer_name") &&
                    (receivableKind === "announcement" ||
                      isReceivableAnnouncementItem(item)) &&
                    canEditReceivableCustomerName(item)
                  ) {
                    return (
                      <ReceivableCustomerNameEditor
                        key={field.key}
                        item={item}
                        label={field.label}
                        value={displayValue}
                        ownerUserId={ownerUserId}
                        workspaceId={workspaceId}
                        disabled={disabled}
                        onApplied={onAmountApplied}
                      />
                    );
                  }

                  if (
                    (field.key === "slip_sender" ||
                      field.key === "slip_sender_name" ||
                      field.key === "sender_name") &&
                    (receivableKind === "slip" || isReceivableSlipItem(item)) &&
                    canEditPendingSlipSender(item)
                  ) {
                    return (
                      <ReceivableSlipSenderEditor
                        key={field.key}
                        item={item}
                        label={field.label}
                        value={displayValue}
                        ownerUserId={ownerUserId}
                        workspaceId={workspaceId}
                        disabled={disabled}
                        onApplied={onAmountApplied}
                      />
                    );
                  }

                  if (!isReceivableFieldEditable(field.key)) {
                    return (
                      <ReceivableFieldReadonly
                        key={field.key}
                        fieldKey={field.key}
                        label={field.label}
                        value={displayValue}
                      />
                    );
                  }

                  return (
                    <ReceivableFieldInput
                      key={field.key}
                      fieldKey={field.key}
                      label={field.label}
                      value={draftValues[field.key] ?? ""}
                      multiline={isMultilineReceivableField(
                        field.key,
                        draftValues[field.key] ?? field.value,
                      )}
                      disabled={disabled}
                      onChange={(value) => handleFieldChange(field.key, value)}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
